import styled from 'styled-components';
import { Flex } from '../../../Utils/styles/display';
import Body from '../../atoms/Body';
import Dropdown from '../../molecules/Dropdown_V2';
import {
  DropdownVariants,
  DropdownColor,
  MenuItemProps,
  DropdownPlacement,
  MenuItemIndividualItem,
} from '../Dropdown_V2/interfaces';
import ThemeV2 from '../../../theme';
import { useState } from 'react';
import { MultiSelectAutoComplete } from '../../organism/Autocomplete';
import IconButton from '../../atoms/IconButton_V2';
import { CustomButtonColorConfig } from '../../atoms/IconButton_V2/interface';
import SVGIcon from '../../atoms/SVGIcon';
import { AutocompleteDropdownItem } from '../../organism/Autocomplete/interfaces';

export const defaultColorConfig: CustomButtonColorConfig = {
  background: ThemeV2.palette.geekBlue6,
  textColor: ThemeV2.palette.gray3,
  borderColor: ThemeV2.palette.gray8,
  hover: {
    background: ThemeV2.palette.geekBlue4,
    textColor: ThemeV2.palette.gray8,
    borderColor: ThemeV2.palette.gray3,
  },
  active: {
    background: ThemeV2.palette.geekBlue6,
    textColor: ThemeV2.palette.gray8,
    borderColor: ThemeV2.palette.gray3,
  },
};

const DepartmentIndicator = styled(Flex)`
  padding: 9px 12px;
  gap: 8px;
  background: ${ThemeV2.palette.gray3};
  border-radius: 4px;
  width: fit-content;
  cursor: default;
`;

export const GroupCriteriaConditionDropdown = styled(Dropdown)`
  button {
    margin: 0 8px;
    border-radius: 4px;
    &:focus-within {
      background: ${ThemeV2.palette.gray3};
    }
  }
`;

const AutoCompleteWrapper = styled.div`
  width: 100%;

  .input-new-line {
    .MuiInputBase-root {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      position: relative;
      .MuiInputBase-input {
        margin-left: 12px;
        width: 80%;
      }

      .autocomplete-loader {
        position: absolute;
        right: 12px;
        bottom: 12px;
        margin: 0;
      }

      .MuiAutocomplete-endAdornment {
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
`;

const SelectButton = styled(IconButton)`
  height: 40px;
  width: 40px;
  margin: 0 8px;
`;

const DeleteButton = styled(IconButton)`
  height: 40px;
  width: 40px;
  margin-right: 8px;
`;

export const SelectIcon = styled(SVGIcon)`
  border-radius: 4px;
`;

export const DeleteIcon = styled(SVGIcon)`
  border-radius: 4px;
  padding: 0 4px 0 2px;
`;

const isConditionMenuOption = {
  value: `is`,
  id: `is`,
};

const isNotConditionMenuOption = {
  value: `is not`,
  id: `isNot`,
};

export const departmentCriteriaConditionItems: MenuItemProps[] = [
  {
    id: 'department-criteria-options',
    items: [isConditionMenuOption, isNotConditionMenuOption],
  },
];

export const DepartmentSelector = ({
  isDepartmentsLoading,
  departments,
  onDepartmentSelect,
  onDepartmentDelete,
}: {
  isDepartmentsLoading: boolean;
  departments: AutocompleteDropdownItem<string>[];
  onDepartmentSelect: (
    department: AutocompleteDropdownItem<string, undefined>[],
    condition: MenuItemIndividualItem,
  ) => void;
  onDepartmentDelete: () => void;
}) => {
  const [condition, setConditionValue] = useState<MenuItemIndividualItem>(
    isConditionMenuOption,
  );
  const [selectedDepartments, setSelectedDepartments] = useState<
    AutocompleteDropdownItem<string>[]
  >([]);

  return (
    <Flex padding="8px" width="100%" alignItems="flex-start">
      <DepartmentIndicator>
        <Body color="gray8" variant="body2Medium">
          Department
        </Body>
      </DepartmentIndicator>
      <GroupCriteriaConditionDropdown
        onItemClick={(v) => {
          setConditionValue(
            v === 'is' ? isConditionMenuOption : isNotConditionMenuOption,
          );
        }}
        menuItems={departmentCriteriaConditionItems}
        variant={DropdownVariants.Text}
        color={DropdownColor.BlueTertiary}
        value={condition.value}
        dropdownPlacement={DropdownPlacement.BottomStart}
      />

      <AutoCompleteWrapper>
        <MultiSelectAutoComplete
          loading={isDepartmentsLoading}
          value={selectedDepartments}
          options={departments}
          onChange={(selectedOptions) => {
            if (selectedOptions) setSelectedDepartments(selectedOptions);
          }}
          placeholder="Choose a department"
          disableCloseOnSelect
          autoFocus
          className="input-new-line"
        />
      </AutoCompleteWrapper>
      <SelectButton
        onClick={() => onDepartmentSelect(selectedDepartments, condition)}
        color="primary"
        variant="contained"
        disabled={selectedDepartments.length === 0}
      >
        <SelectIcon
          icon="check-rounded"
          size="28px"
          data-testid="SVGIcon"
          data-qa-id="icon-svg"
        />
      </SelectButton>
      <DeleteButton onClick={onDepartmentDelete} color="text">
        <DeleteIcon
          icon="delete"
          size="24px"
          data-testid="SVGIcon"
          data-qa-id="icon-svg"
        />
      </DeleteButton>
    </Flex>
  );
};
